import { LocationInputV2 } from '@shared/components/LocationInputV2';
import type { LocationInputV2Response } from '@shared/components/LocationInputV2/LocationInputV2.types';
import { withWindow } from '@shared/utils/withWindow';
import { FormControl, Box, Flex, TextV2, styled } from '@withjoy/joykit';
import { animationTransition } from '@shared/joykit/packages/core/utils';
import { MapMarker } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useField } from 'formik';
import React from 'react';
import { useTranslation } from '@shared/core';
import * as Yup from 'yup';
import { useSearchFormConfig } from '../useSearchFormConfig';

const LOCATION_INPUT_FONT_SIZE = 13;
const StyledLocationContainer = styled(Box)`
  div[aria-haspopup='listbox'] {
    border-radius: ${pxToRem(16)};
  }

  div[aria-haspopup='listbox'] input {
    font-size: ${pxToRem(LOCATION_INPUT_FONT_SIZE)} !important;
    text-overflow: ellipsis;
    padding-left: ${pxToRem(48)} !important;
    margin-top: ${pxToRem(4)} !important;
  }

  div[aria-haspopup='listbox'] input:focus,
  div[aria-haspopup='listbox'] input:active {
    padding-left: ${pxToRem(48)} !important;
    margin-top: ${pxToRem(4)} !important;
  }

  div[aria-haspopup='listbox'] input::placeholder {
    font-size: ${pxToRem(LOCATION_INPUT_FONT_SIZE)};
  }

  /* Ensure icon stays in place */
  div[aria-haspopup='listbox'] > svg {
    position: absolute !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    pointer-events: none !important;
  }
`;

/**
 * The mobile sheet's search pill, carried over VERBATIM from `HotelsSearchInput` so the sheet looks
 * exactly as it did before this field stopped using that component.
 *
 * Every value here — the 64px height, the 32px radius, the two-layer shadow, the `mono5` hairline
 * and the `#5f269b` hover/focus treatment — is copied from that component's wrapper rather than
 * re-derived, because the goal was to change what the input *stores*, not how it looks.
 *
 * The brand purple and the raw shadow are pre-existing, not new: a field taking a brand colour is
 * against the repo's field-focus contract ("fields are neutral, actions are branded"), and there is
 * no token for either value. Both are reproduced as-is so this is a behaviour change and not a
 * silent restyle; correcting them is a design decision for whoever owns this pill.
 */
const MOBILE_PILL_SHADOW = '0px 4px 16px 0px rgba(0, 0, 0, 0.07), 0px 7px 27px 0px rgba(44, 41, 37, 0.06)';
const MOBILE_PILL_ACCENT = '#5f269b';

/**
 * Refits `LocationInputV2` — a standalone 48px field — into the pill's lower half, under the label.
 *
 * Everything here undoes a dimension the field hardcodes for the standalone case. Each override
 * names the line it is fighting, because none of them are cosmetic preference:
 *
 * 1. HEIGHT. The field ships an intrinsic ~48px, and the pill holds the label AND the field, so at
 *    its natural height the pair overflows and the label rides up through the pill's top border.
 *    Same override `HotelsSearchInput` applied to its own inner input.
 *
 * 2. LEFT PADDING. `InputV1` sets `style={{ paddingLeft: iconLeftWidth, paddingRight: … }}` on the
 *    input — an INLINE style, from state that starts at `16` and is only re-measured when an icon
 *    exists (`InputV1/index.tsx`). With no `iconLeft` the input therefore keeps a 16px indent that
 *    no stylesheet can outrank, which is what left "Location" hanging to the left of the value.
 *    `SelectV1`'s `Placeholder` hardcodes the matching `left: 16px` (`SelectV1/styles.tsx`), so the
 *    empty state needs the same correction or the placeholder and the value sit at different x.
 *    The pill owns the horizontal rhythm out here; the field must not add its own.
 *
 * 3. ICON BOX HEIGHT. The clear affordance's container is `position: absolute; top: 0;
 *    min-height: 48px` (`InputV1/StyledInputIcon.tsx`) — sized for the standalone field. Inside the
 *    collapsed one it stays 48px and centres the X against that, dropping it below the value's
 *    baseline. Released to `height: 100%`, it centres on the text.
 *
 * The 16px font is `HotelsSearchInput`'s, and matters on iOS besides: below 16px Safari zooms the
 * page on focus.
 *
 * NOTHING here touches the map pin. The pill renders it as a sibling of this container; see the
 * note at that element for why it cannot live inside the field.
 */
const StyledMobileLocationContainer = styled(Box)`
  flex-grow: 1;
  min-width: 0;

  div[aria-haspopup='listbox'] {
    min-height: auto;
    height: auto;
  }

  div[aria-haspopup='listbox'] input {
    font-size: ${pxToRem(16)} !important;
    min-height: auto !important;
    height: auto !important;
    /* Beats InputV1's inline paddingLeft — see (2). The right padding is left alone: it is the
       measured width of the clear button, and without it the value runs underneath the X. */
    padding-left: 0 !important;
    text-overflow: ellipsis;
  }

  /* The placeholder, which SelectV1 positions absolutely at the same 16px — see (2).
     Second div inside the value container; the first is the input's own wrapper. */
  div[aria-haspopup='listbox'] > div > div + div {
    left: 0;
  }

  /* The clear button's box — see (3). The icon containers are the only spans in this subtree. */
  div[aria-haspopup='listbox'] span {
    min-height: auto;
  }
`;

const schema = Yup.object({
  displayName: Yup.string(),
  latitude: Yup.number(),
  longitude: Yup.number(),
  googlePlaceId: Yup.string(),
}).test('location', 'Invalid location', function (value) {
  if (!value) {
    return false;
  }
  return value.displayName && value.latitude && value.longitude;
});
type ISchema = {
  displayName: string;
  latitude: number;
  longitude: number;
  googlePlaceId: string;
};

const initialValue = {
  displayName: '',
  googlePlaceId: '',
  latitude: undefined,
  longitude: undefined,
};

const Fields = ({ fieldsId }: { fieldsId: string }) => {
  const { tq } = useTranslation('accommodationsConsole');
  // avoid passing value because I want LocationInputV2 to be an uncontrolled component, we just want the location info
  const [{ value, ...restField }, meta, helper] = useField<Partial<ISchema>>(fieldsId);
  const { isOpenedFormView, isCompactVersion } = useSearchFormConfig();
  const onChange = (response: LocationInputV2Response | null) => {
    if (!response) {
      // Reset formik state so stale coordinates don't pass validation on clear.
      helper.setValue({ displayName: '', latitude: undefined, longitude: undefined, googlePlaceId: '' });
      return;
    }
    helper.setValue({
      displayName: response.displayName,
      latitude: response.lat,
      longitude: response.lng,
      googlePlaceId: response.placeId,
    });
    // This code is added to lose focus, especially on mobile, after searching for results.
    withWindow(() => {
      if (document.activeElement) {
        const activeElement = document.activeElement as HTMLElement;

        activeElement?.blur();
      }
    });
  };
  // Yup's object-level .test() produces an object error, not a string;
  // coerce so FormControl doesn't try to render an object as a React child.
  const errorMessage = meta.touched ? (typeof meta.error === 'string' ? meta.error : meta.error ? tq`Invalid location` : undefined) : undefined;

  /**
   * Shared where it decides BEHAVIOUR, per-breakpoint where it decides looks.
   *
   * The two breakpoints used to be different components, which is how they came to store different
   * things — so everything governing what gets stored is settled once, here. Presentation is not:
   * these are two differently shaped controls, and the desktop pill's chrome does not transfer.
   */
  const locationInputProps = {
    ...restField,
    iconRight: null,
    noBorder: true,
    placePredictionTypes: [],
    // Free text resolves to no coordinates, which this field's schema rejects — only offer real predictions.
    allowCustomPlace: false,
    isHideListItemIcon: true,
    defaultValue: value.displayName,
    onLocationChange: onChange,
    includeHotelIcon: false,
  };

  return (
    <FormControl
      {...meta}
      zIndex="banner"
      width="100%"
      maxWidth={isCompactVersion ? 'full' : { _: 'full', md: pxToRem(260) }}
      position={isCompactVersion ? 'static' : { _: 'static', md: 'absolute' }}
      top={0}
      isInvalid={!!errorMessage}
      error={errorMessage}
    >
      {/* ONE input on both breakpoints, in the styling each already had.

          The mobile sheet used to render `HotelsSearchInput`, and the two components disagreed about
          what they wrote into the same field. `displayName` becomes the `destination` query param and
          then `state.searchLocation`; `LocationInputV2` stores the prediction's
          `structured_formatting.main_text` ("JW Marriott Chicago") while `HotelsSearchInput` stored
          the geocoded `formattedAddress` ("JW Marriott Chicago, West Adams Street, Chicago, IL,
          USA"). Same hotel, same coordinates, same results — two labels, split by device.

          That reached the featured band's heading, every tile's "X mi from …", the shared URL, and
          the `destination` on `destinationSearched`, where it split one place into two rows along
          precisely the mobile/desktop axis the featured-placement arms are compared across.

          The fork is deleted rather than kept in sync, so there is no second label left to drift.
          The sheet keeps its pill: `HotelsSearchInput` rendered no button here (this call passed
          `hideIcon`) and always rendered the input branch (`isEditing` starts `!showRoomBlocks`, and
          `showRoomBlocks` was never passed), so the pill and its label are the whole of what it
          contributed visually. */}
      {isOpenedFormView ? (
        <Flex
          width="100%"
          // `minHeight`, not a fixed `height`. 64px is the pill's designed size, but as a hard
          // height the label and field overflow it the moment the input is taller than expected —
          // which is exactly how the label came to sit across the top border. A floor keeps the
          // intended size without letting the control's own contents break out of it.
          minHeight={pxToRem(64)}
          columnGap={pxToRem(16)}
          paddingY={3}
          paddingX={6}
          alignItems="center"
          borderRadius={pxToRem(32)}
          border="1px solid"
          borderColor="mono5"
          boxShadow={MOBILE_PILL_SHADOW}
          _hover={{ borderColor: MOBILE_PILL_ACCENT }}
          _focusWithin={{ boxShadow: `${MOBILE_PILL_SHADOW}, 0px 0 0px 1px ${MOBILE_PILL_ACCENT}` }}
          transition={animationTransition('border-color', 'box-shadow')}
          zIndex="banner"
        >
          {/* The pin is a SIBLING of the label+field column, not a prop of the field.

              Inside the field it is centred against the field alone — which sits below the label —
              so it rides low, and it indents the field's text past the label, leaving "Location"
              hanging to the left of the property name. Neither is fixable from the field's own CSS,
              because the pin is in the wrong box. Out here it centres on the whole pill, and the
              label and the value share one left edge. */}
          <MapMarker
            size="md"
            color="mono10"
          />

          <Flex
            flexDirection="column"
            justifyContent="center"
            flexGrow={1}
            minWidth={0}
          >
            <TextV2
              typographyVariant="label3"
              color="mono10"
              marginBottom={pxToRem(2)}
            >
              {tq`Location`}
            </TextV2>
            <StyledMobileLocationContainer>
              <LocationInputV2
                {...locationInputProps}
                // The pill renders the pin itself, above — see the note there.
                includeMapIcon={false}
                // The sheet's own wording, not the desktop pill's example venue.
                placeholder={tq`Search for a city or venue`}
                // The pill draws the focus indicator itself, via `_focusWithin` above — exactly as
                // `HotelsSearchInput` did. Without this the field's own ring paints inside it too.
                suppressFocusRing
              />
            </StyledMobileLocationContainer>
          </Flex>
        </Flex>
      ) : (
        <StyledLocationContainer>
          <LocationInputV2
            {...locationInputProps}
            includeMapIcon
            placeholder={tq`Center Park Boathouse`}
          />
        </StyledLocationContainer>
      )}
    </FormControl>
  );
};

export { Fields, schema, initialValue };
